import { animated, useSpring, useTransition } from "@react-spring/web";
import type { Nudge } from "@repo/app-types/entities/liveCoaching";
import { type FC, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface NudgeWidgetProps {
  activeNudge: Nudge | undefined;
}

const SPRING_CONFIG = { tension: 280, friction: 28 };

const NudgeCard: FC<{ nudge: Nudge }> = ({ nudge }) => (
  <div className="pb-3">
    <div
      className={twMerge(
        "rounded-xl border px-4 py-3.5",
        nudge.level === "urgent"
          ? "border-rose-200 bg-rose-50/95"
          : "border-indigo-200/90 bg-indigo-50/95",
      )}
    >
      <p className="text-sm leading-snug font-medium text-foreground">
        {nudge.title}
      </p>

      {nudge.suggestedWording ? (
        <div className="mt-2.5 rounded-lg bg-white/70 px-3 py-2.5">
          <p className="text-sm leading-relaxed text-muted-foreground italic">
            &ldquo;{nudge.suggestedWording}&rdquo;
          </p>
        </div>
      ) : null}
    </div>
  </div>
);

export const NudgeWidget: FC<NudgeWidgetProps> = ({ activeNudge }) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    setMeasuredHeight(el.offsetHeight);

    const observer = new ResizeObserver(() => {
      if (measureRef.current) {
        setMeasuredHeight(measureRef.current.offsetHeight);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const containerSpring = useSpring({
    height: activeNudge ? measuredHeight : 0,
    config: SPRING_CONFIG,
  });

  const transitions = useTransition(activeNudge ?? null, {
    keys: (nudge) => nudge?.id ?? "__empty__",
    from: { opacity: 0, y: -6 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: -6 },
    config: SPRING_CONFIG,
  });

  return (
    <animated.div
      aria-live="polite"
      className="relative -mt-2 mb-3 overflow-hidden"
      style={{ height: containerSpring.height }}
    >
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute inset-x-0 top-2"
      >
        {activeNudge ? <NudgeCard nudge={activeNudge} /> : null}
      </div>

      {transitions((style, nudge) =>
        nudge ? (
          <animated.div
            className="absolute inset-x-0 top-2"
            style={{
              opacity: style.opacity,
              transform: style.y.to((v) => `translate3d(0, ${v}px, 0)`),
            }}
          >
            <NudgeCard nudge={nudge} />
          </animated.div>
        ) : null,
      )}
    </animated.div>
  );
};
